import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Section } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Sparkles, ArrowLeft, MessageSquare, Trash2, Archive, Users as UsersIcon, Play, Pause, Square, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTimer } from "@/context/TimerContext";
import { Checkbox } from "@/components/ui/checkbox";

const COLUMNS = [
  { key: "todo", label: "Backlog" },
  { key: "in_progress", label: "In progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];

const PRIORITY_COLORS = {
  low: "bg-neutral-100 text-neutral-600",
  medium: "bg-blue-50 text-blue-700",
  high: "bg-amber-50 text-amber-700",
  urgent: "bg-red-50 text-red-700",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", status: "todo", assignee_id: "", due_date: "", estimated_hours: 0, quantity: 1, benchmark_per_unit: 0, template_id: "" });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [templates, setTemplates] = useState([]);

  const isSuperAdmin = me?.role === "super_admin";
  const isAdmin = me?.role === "admin" || isSuperAdmin;
  const { timer, start: startTimer, pause: pauseTimer, resume: resumeTimer, stop: stopTimer } = useTimer();
  const isProjectActive = project?.status === "active" && !project?.archived;
  const activeTaskTimer = timer && timer.task_id === activeTask?.id;

  // Members assigned to this project (or all users if none assigned yet)
  const [hoursByTask, setHoursByTask] = useState({});
  const [taskEntries, setTaskEntries] = useState([]); // time entries for the active task in dialog

  const assignedIds = project?.assigned_user_ids || [];
  const assignedUsers = users.filter(u => assignedIds.includes(u.id));
  const assignableUsers = assignedUsers.length > 0 ? assignedUsers : users;

  const load = async () => {
    try {
      const [p, t, u, tmpls, te] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/tasks?project_id=${id}`),
        api.get("/users"),
        api.get("/task-templates"),
        api.get(`/time-entries?project_id=${id}`),
      ]);
      setProject(p.data); setTasks(t.data); setUsers(u.data); setTemplates(tmpls.data);
      const totals = {};
      for (const e of te.data || []) {
        if (e.task_id) totals[e.task_id] = (totals[e.task_id] || 0) + Number(e.hours || 0);
      }
      setHoursByTask(totals);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => { load(); }, [id]);

  const createTask = async () => {
    try {
      const payload = {
        ...form, project_id: id,
        estimated_hours: parseFloat(form.estimated_hours) || 0,
        quantity: parseInt(form.quantity) || 1,
        benchmark_per_unit: parseFloat(form.benchmark_per_unit) || 0,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date || null,
        template_id: form.template_id || null,
      };
      await api.post("/tasks", payload);
      toast.success("Task created");
      setOpen(false);
      setForm({ title: "", description: "", priority: "medium", status: "todo", assignee_id: "", due_date: "", estimated_hours: 0, quantity: 1, benchmark_per_unit: 0, template_id: "" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const applyTemplate = (templateId) => {
    if (!templateId || templateId === "_none") {
      setForm({ ...form, template_id: "" });
      return;
    }
    const t = templates.find(x => x.id === templateId);
    if (!t) return;
    setForm({
      ...form,
      template_id: templateId,
      title: form.title || t.name,
      benchmark_per_unit: t.benchmark_per_unit,
    });
  };

  const archiveProject = async () => {
    try {
      const r = await api.post(`/projects/${id}/archive`);
      setProject(r.data);
      toast.success(r.data.archived ? "Project archived" : "Project restored");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteProject = async () => {
    setDeleting(true);
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      navigate("/projects");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  const [openMembers, setOpenMembers] = useState(false);
  const [memberDraft, setMemberDraft] = useState([]);

  const openMembersDialog = () => {
    setMemberDraft(project.assigned_user_ids || []);
    setOpenMembers(true);
  };

  const toggleMemberDraft = (uid) => {
    setMemberDraft(d => d.includes(uid) ? d.filter(x => x !== uid) : [...d, uid]);
  };

  const saveMembers = async () => {
    try {
      const r = await api.put(`/projects/${id}/members`, { user_ids: memberDraft });
      setProject(r.data);
      toast.success("Project team updated");
      setOpenMembers(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const moveTask = async (taskId, status) => {
    await api.put(`/tasks/${taskId}`, { status });
    load();
  };

  const openTask = async (t) => {
    setActiveTask(t);
    const [comm, entries] = await Promise.all([
      api.get(`/comments?task_id=${t.id}`),
      api.get(`/time-entries?project_id=${id}`),
    ]);
    setComments(comm.data);
    setTaskEntries((entries.data || []).filter(e => e.task_id === t.id));
  };

  const refreshTaskEntries = async () => {
    if (!activeTask) return;
    const r = await api.get(`/time-entries?project_id=${id}`);
    const forTask = (r.data || []).filter(e => e.task_id === activeTask.id);
    setTaskEntries(forTask);
    // Also refresh totals grid
    const totals = { ...hoursByTask };
    totals[activeTask.id] = forTask.reduce((s, e) => s + Number(e.hours || 0), 0);
    setHoursByTask(totals);
  };

  // Manual hours state
  const [manualOpen, setManualOpen] = useState(false);
  const [manualHours, setManualHours] = useState(1);
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [manualDesc, setManualDesc] = useState("");

  const addManualHours = async () => {
    const hrs = parseFloat(manualHours);
    if (!hrs || hrs <= 0) { toast.error("Hours must be > 0"); return; }
    try {
      await api.post("/time-entries", {
        project_id: id,
        task_id: activeTask.id,
        hours: hrs,
        date: manualDate,
        description: manualDesc,
        billable: true,
      });
      toast.success(`Logged ${hrs}h`);
      setManualOpen(false);
      setManualHours(1); setManualDesc("");
      refreshTaskEntries();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteEntry = async (entryId) => {
    try {
      await api.delete(`/time-entries/${entryId}`);
      toast.success("Entry removed");
      refreshTaskEntries();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    await api.post("/comments", { task_id: activeTask.id, body: newComment });
    setNewComment("");
    const r = await api.get(`/comments?task_id=${activeTask.id}`);
    setComments(r.data);
  };

  const deleteTask = async (taskId) => {
    await api.delete(`/tasks/${taskId}`);
    setActiveTask(null);
    load();
  };

  const generateSummary = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/ai/project-summary/${id}`);
      setAiSummary(r.data.summary);
      toast.success("AI summary generated");
    } catch (e) { toast.error("AI failed"); }
    finally { setAiLoading(false); }
  };

  if (!project) return <div className="p-8 text-sm text-neutral-500">Loading…</div>;

  const tasksByCol = COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: tasks.filter(t => t.status === c.key) }), {});

  return (
    <div data-testid="project-detail-page">
      <PageHeader
        eyebrow={<span><Link to="/projects" className="hover:underline">Tasks</Link> / detail{project.archived && " · archived"}</span>}
        title={project.name}
        description={project.description || "—"}
        testid="project-detail-header"
        actions={
          <>
            {isSuperAdmin && (
              <Button onClick={archiveProject} variant="outline" className="rounded-sm" data-testid="archive-project-btn">
                <Archive size={14} className="mr-1" /> {project.archived ? "Restore" : "Archive"}
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="outline"
                className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="delete-project-btn"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
            <Button onClick={generateSummary} disabled={aiLoading} variant="outline" className="rounded-sm" data-testid="ai-summary-btn">
              <Sparkles size={14} className="mr-1" /> {aiLoading ? "Thinking…" : "AI summary"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="new-task-button">
                  <Plus size={14} className="mr-1" /> New activity
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg rounded-sm">
                <DialogHeader><DialogTitle>Create activity</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {templates.length > 0 && (
                    <div>
                      <Label>Pick a benchmark template <span className="text-neutral-400 font-normal">(optional)</span></Label>
                      <Select value={form.template_id || "_none"} onValueChange={applyTemplate}>
                        <SelectTrigger data-testid="task-template-select"><SelectValue placeholder="Custom (no template)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Custom · no template</SelectItem>
                          {templates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.category} · {t.name} · {t.benchmark_per_unit}h/unit</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
                  <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="task-desc-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Priority</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["low", "medium", "high", "urgent"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Assignee</Label>
                      <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                        <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                        <SelectContent>
                          {assignableUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="task-duedate-input" /></div>
                    <div><Label>Est. hours</Label><Input type="number" step="0.5" value={form.estimated_hours} onChange={e => setForm({ ...form, estimated_hours: e.target.value })} data-testid="task-est-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Quantity</Label>
                      <Input type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} data-testid="task-qty-input" />
                      <div className="text-[11px] text-neutral-500 mt-1">e.g. 3 for "Reel Edit ×3"</div>
                    </div>
                    <div>
                      <Label>Benchmark / unit (h)</Label>
                      <Input type="number" step="0.25" value={form.benchmark_per_unit} onChange={e => setForm({ ...form, benchmark_per_unit: e.target.value })} data-testid="task-bench-input" />
                      <div className="text-[11px] text-neutral-500 mt-1">Effective: {((parseInt(form.quantity) || 1) * (parseFloat(form.benchmark_per_unit) || 0)).toFixed(2)}h</div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createTask} disabled={!form.title} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="task-create-submit">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <Section className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
        {[
          { l: "Status", v: project.status.replace("_", " ") },
          { l: "Activities", v: tasks.length },
          { l: "Start date", v: project.start_date || "—" },
          { l: "End date", v: project.end_date || "—" },
        ].map((s) => (
          <div key={s.l} className="bg-white p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2">{s.l}</div>
            <div className="font-display text-xl font-bold">{s.v}</div>
          </div>
        ))}
      </Section>

      <Section>
        <div className="border border-neutral-200 bg-white" data-testid="project-team-card">
          <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">Project team</div>
              <div className="font-display text-lg font-bold">{assignedUsers.length === 0 ? "Whole studio" : `${assignedUsers.length} ${assignedUsers.length === 1 ? "member" : "members"}`}</div>
            </div>
            {isAdmin && (
              <Button onClick={openMembersDialog} variant="outline" className="rounded-sm" data-testid="manage-members-btn">
                <UsersIcon size={14} className="mr-1" /> Manage
              </Button>
            )}
          </div>
          <div className="p-5">
            {assignedUsers.length === 0 ? (
              <div className="text-xs text-neutral-500">No specific allocation — anyone can be assigned tasks on this project. Click <span className="font-semibold text-neutral-700">Manage</span> to lock it down to a team.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {assignedUsers.map(u => (
                  <div key={u.id} className="flex items-center gap-2 border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm" data-testid={`project-member-${u.id}`}>
                    <div className="w-6 h-6 rounded-sm bg-neutral-900 text-white grid place-items-center text-[10px] font-semibold">{(u.name || u.email).slice(0, 1).toUpperCase()}</div>
                    <span>{u.name || u.email}</span>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">{u.role.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      {aiSummary && (
        <Section>
          <div className="border border-neutral-900 bg-neutral-50 p-6" data-testid="ai-summary-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} />
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold">AI delivery summary · Claude Sonnet 4.5</div>
            </div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{aiSummary}</div>
          </div>
        </Section>
      )}

      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200" data-testid="kanban-board">
          {COLUMNS.map(col => (
            <div key={col.key} className="bg-white min-h-[400px]" data-testid={`kanban-col-${col.key}`}>
              <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold">{col.label}</div>
                <div className="text-xs text-neutral-500 font-mono">{tasksByCol[col.key].length}</div>
              </div>
              <div className="p-3 space-y-2">
                {tasksByCol[col.key].map(t => {
                  const eff = (t.quantity || 1) * (t.benchmark_per_unit || 0);
                  const logged = hoursByTask[t.id] || 0;
                  return (
                  <button
                    key={t.id}
                    onClick={() => openTask(t)}
                    className="w-full text-left border border-neutral-200 bg-white p-3 hover:border-neutral-400 transition-colors"
                    data-testid={`task-card-${t.id}`}
                  >
                    <div className="text-sm font-medium leading-snug mb-2">
                      {t.title}
                      {(t.quantity || 1) > 1 && <span className="ml-1 text-neutral-500 font-mono">×{t.quantity}</span>}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                      <div className="flex items-center gap-1.5">
                        {logged > 0 && (
                          <span className="text-[10px] text-emerald-700 font-mono font-semibold" data-testid={`task-logged-${t.id}`} title="Total hours logged">
                            {logged.toFixed(1)}h
                          </span>
                        )}
                        {eff > 0 ? (
                          <span className="text-[10px] text-neutral-500 font-mono">/ {eff.toFixed(1)}h</span>
                        ) : t.due_date ? (
                          <span className="text-[10px] text-neutral-500 font-mono">{t.due_date}</span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                  );
                })}
                <div className="flex flex-wrap gap-1 pt-1">
                  {COLUMNS.filter(c => c.key !== col.key).map(c => null)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Task Detail */}
      <Dialog open={!!activeTask} onOpenChange={(v) => !v && setActiveTask(null)}>
        <DialogContent className="sm:max-w-xl rounded-sm" data-testid="task-detail-dialog">
          {activeTask && (
            <>
              <DialogHeader>
                <DialogTitle>{activeTask.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-neutral-600">{activeTask.description || "No description."}</p>

                {/* Timer controls */}
                {activeTask.assignee_id === me?.id && !isAdmin && !isProjectActive && (
                  <div className="border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2 rounded-sm">
                    Time can only be logged on <b>Active</b> tasks. This task is <b>{project.status?.replace("_", " ")}</b>.
                  </div>
                )}
                {(activeTask.assignee_id === me?.id || isAdmin) && (
                  <div className="flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
                    {!activeTaskTimer ? (
                      <Button
                        onClick={async () => {
                          try {
                            await startTimer({ project_id: id, task_id: activeTask.id, description: activeTask.title });
                            if (activeTask.status === "todo") {
                              setActiveTask({ ...activeTask, status: "in_progress" }); // flipped server-side by timer start
                            }
                          } catch (e) { console.warn("[ProjectDetail] start work failed", e); /* toast handled in context */ }
                        }}
                        disabled={!isProjectActive}
                        className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                        data-testid="activity-start-work"
                      >
                        <Play size={12} className="mr-1" /> Start work
                      </Button>
                    ) : (
                      <>
                        {timer.is_paused ? (
                          <Button onClick={resumeTimer} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs" data-testid="activity-resume">
                            <Play size={12} className="mr-1" /> Resume
                          </Button>
                        ) : (
                          <Button onClick={pauseTimer} className="rounded-sm bg-neutral-700 hover:bg-neutral-600 text-white h-8 text-xs" data-testid="activity-pause">
                            <Pause size={12} className="mr-1" /> Pause
                          </Button>
                        )}
                        <Button onClick={stopTimer} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white h-8 text-xs" data-testid="activity-stop">
                          <Square size={12} className="mr-1" /> Stop & log
                        </Button>
                        <span className="text-[11px] text-neutral-500">
                          {timer.is_paused ? "Paused — clock frozen" : "Timer running…"}
                        </span>
                      </>
                    )}
                  </div>
                )}

                {isAdmin && (
                  <div className="flex flex-wrap gap-2">
                    {COLUMNS.map(c => (
                      <Button
                        key={c.key}
                        variant={activeTask.status === c.key ? "default" : "outline"}
                        onClick={() => moveTask(activeTask.id, c.key).then(() => setActiveTask({ ...activeTask, status: c.key }))}
                        className="rounded-sm text-xs h-8"
                        data-testid={`move-${c.key}`}
                      >
                        Move to {c.label}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="border-t border-neutral-200 pt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2"><Clock3 size={12} /> Time logged · {(hoursByTask[activeTask.id] || 0).toFixed(1)}h</span>
                    {(isAdmin || activeTask.assignee_id === me?.id) && isProjectActive && (
                      <button
                        onClick={() => setManualOpen(true)}
                        className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-900 hover:underline"
                        data-testid="add-manual-hours-btn"
                      >
                        + Add hours manually
                      </button>
                    )}
                  </div>
                  {taskEntries.length === 0 ? (
                    <div className="text-xs text-neutral-500 mb-3">No time logged yet.</div>
                  ) : (
                    <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                      {taskEntries.map(e => {
                        const u = users.find(x => x.id === e.user_id);
                        const canDelete = isAdmin || e.user_id === me?.id;
                        return (
                          <div key={e.id} className="text-xs flex items-center justify-between bg-neutral-50 border border-neutral-200 px-3 py-2" data-testid={`entry-row-${e.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="font-mono font-semibold text-emerald-700 shrink-0">{Number(e.hours).toFixed(2)}h</span>
                              <span className="text-neutral-500 shrink-0">{e.date}</span>
                              <span className="text-neutral-700 truncate">{u?.name || u?.email || "—"}</span>
                              {e.description && <span className="text-neutral-400 truncate">· {e.description}</span>}
                            </div>
                            {canDelete && (
                              <button onClick={() => deleteEntry(e.id)} className="text-neutral-400 hover:text-rose-600" data-testid={`entry-delete-${e.id}`} title="Delete entry">
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="border-t border-neutral-200 pt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-2"><MessageSquare size={12} /> Comments</div>
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {comments.map(c => (
                      <div key={c.id} className="text-sm bg-neutral-50 p-3 border border-neutral-200" data-testid={`comment-${c.id}`}>
                        <div className="text-xs font-semibold mb-1">{c.author_name}</div>
                        <div>{c.body}</div>
                      </div>
                    ))}
                    {comments.length === 0 && <div className="text-xs text-neutral-500">No comments yet.</div>}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment" data-testid="comment-input" />
                    <Button onClick={addComment} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="comment-submit">Post</Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="destructive" onClick={() => deleteTask(activeTask.id)} className="rounded-sm" data-testid="task-delete-btn">
                  <Trash2 size={14} className="mr-1" /> Delete task
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual hours dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-sm rounded-sm" data-testid="manual-hours-dialog">
          <DialogHeader>
            <DialogTitle>Log hours manually</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-neutral-500">
              Add time worked on <span className="font-semibold text-neutral-900">{activeTask?.title}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hours</Label>
                <Input type="number" min="0.1" step="0.25" value={manualHours}
                       onChange={e => setManualHours(e.target.value)} data-testid="manual-hours-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={manualDate}
                       onChange={e => setManualDate(e.target.value)} data-testid="manual-date-input" />
              </div>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={manualDesc} onChange={e => setManualDesc(e.target.value)}
                     placeholder="What did you work on?" data-testid="manual-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-sm" data-testid="manual-cancel">Cancel</Button>
            <Button onClick={addManualHours} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="manual-submit">Log hours</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project members dialog */}
      <Dialog open={openMembers} onOpenChange={setOpenMembers}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="members-dialog">
          <DialogHeader><DialogTitle>Manage project team</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            <div className="text-xs text-neutral-500 mb-2">
              Pick teammates who'll work on <span className="font-semibold text-neutral-900">{project.name}</span>. Only allocated members can be picked as task assignees. Leave empty to allow anyone.
            </div>
            {users.map(u => {
              const checked = memberDraft.includes(u.id);
              return (
                <label
                  key={u.id}
                  className={`flex items-center gap-3 px-3 py-2 border rounded-sm cursor-pointer transition-colors ${checked ? "border-neutral-900 bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}
                  data-testid={`member-toggle-${u.id}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggleMemberDraft(u.id)} />
                  <div className="w-7 h-7 rounded-sm bg-neutral-900 text-white grid place-items-center text-[10px] font-semibold">{(u.name || u.email).slice(0, 1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.name || "—"}</div>
                    <div className="text-[11px] text-neutral-500 font-mono truncate">{u.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500">{u.role.replace("_", " ")}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={saveMembers} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="members-save-btn">Save team ({memberDraft.length})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="delete-project-dialog">
          <DialogHeader>
            <DialogTitle>Delete this project?</DialogTitle>
            <DialogDescription>
              Removing <span className="font-semibold">{project.name}</span> can&apos;t be undone.
              {tasks.length > 0 && (
                <span className="block mt-2 text-rose-700 text-sm">
                  ⚠ {tasks.length} {tasks.length === 1 ? "task" : "tasks"} attached to this project will also be deleted.
                  Logged time entries will remain in the database but lose their project link.
                </span>
              )}
              <span className="block mt-2 text-neutral-500 text-xs">
                Tip: if you just want to hide it, use <span className="font-medium">Archive</span> instead.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={deleteProject}
              disabled={deleting}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-delete-project-btn"
            >
              {deleting ? "Deleting…" : "Delete project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
